import { Network, type ConnectionStatus, type ConnectionType } from '@capacitor/network'
import { defineStore } from 'pinia'
import { $fetch } from 'ofetch'
import { computed, ref } from 'vue'
import { useApiBaseUrl } from '~~/layers/base/composables/useApiBaseUrl'
import {
    CONNECTIVITY_PING_PATH,
    nextConnectivityPingDelayMs,
} from '~~/layers/base/utils/connectivityPingBackoff'
import { isLikelyClientTransportError } from '~~/layers/base/utils/fetchAbort'
import { mergeCapacitorDesignerFetchOptions } from '~~/layers/base/utils/mergeCapacitorDesignerFetchOptions'
import { promiseWithTimeout } from '~~/layers/base/utils/promiseWithTimeout'

/** Native `getStatus` must not hang cold start; the iOS bridge is serialized. */
const NETWORK_STATUS_TIMEOUT_MS = 400

/**
 * What produced the latest {@link useNetworkStore.connected} value.
 *
 * - `capacitor-network` — `@capacitor/network` initial read or `networkStatusChange` listener
 * - `capacitor-resume` — `@capacitor/app` foreground re-check (`recheckConnectivity`)
 * - `api-fetch` — `useApiFetch` transport failure or HTTP response
 * - `recovery-ping` — the store's own reachability probe
 */
export type ConnectivityChangeSource =
    | 'capacitor-network'
    | 'capacitor-resume'
    | 'api-fetch'
    | 'recovery-ping'

/**
 * Live connectivity for the Capacitor shell.
 *
 * Two inputs drive {@link useNetworkStore.connected}:
 * 1. Capacitor `@capacitor/network` (OS link state) — OS-offline forces offline; OS-online alone
 *    never flips us back online (link-up ≠ API reachability).
 * 2. `useApiFetch` via {@link notifyApiFetchReachable} / {@link notifyApiFetchTransportFailure} —
 *    transport failures mark offline; any HTTP response (or a successful recovery ping) marks online.
 *
 * While offline, pings `GET /api/handwriting_stationery/signature-stub` with exponential backoff
 * capped at one attempt every other second until the API is reachable again.
 */
export const useNetworkStore = defineStore('network', () => {
    /**
     * Optimistic until the first Capacitor status / transport signal — avoids a false offline
     * flash on cold start.
     */
    const connected = ref(true)
    /** Connection medium from Capacitor; `'unknown'` until the first status read. */
    const connectionType = ref<ConnectionType>('unknown')
    /** Which input produced the current {@link connected} value; `null` before the first flip. */
    const lastChangeSource = ref<ConnectivityChangeSource | null>(null)

    /** Full Capacitor-shaped snapshot; `connected` here is API-aware, not OS-only. */
    const status = computed<ConnectionStatus>(() => ({
        connected: connected.value,
        connectionType: connectionType.value,
    }))

    /**
     * True when the current value came from a native plugin rather than API traffic.
     *
     * Lets the Capacitor shell surface *how* connectivity was detected: the OS plugins report a
     * dropped interface with no app activity, whereas `api-fetch` / `recovery-ping` only speak
     * after a request was attempted.
     */
    const changedByNativePlugin = computed(
        () =>
            lastChangeSource.value === 'capacitor-network' ||
            lastChangeSource.value === 'capacitor-resume'
    )

    /**
     * Records the source then commits the value, so watchers reading both always see a matching
     * pair. No-ops when the value is unchanged: repeat signals from the same state must not
     * re-trigger connectivity toasts.
     */
    function setConnected(next: boolean, source: ConnectivityChangeSource): void {
        if (connected.value === next) {
            return
        }
        lastChangeSource.value = source
        connected.value = next
    }

    /** True while a recovery ping loop is scheduled or in flight. */
    let pingLoopActive = false
    /** Pings already failed in the current offline spell (feeds backoff). */
    let failedPingCount = 0
    let pingTimer: ReturnType<typeof setTimeout> | null = null
    let pingInFlight = false

    /**
     * Stops the recovery ping loop and clears backoff state.
     *
     * Early-returns when already idle: `reportReachable` runs on every successful API response, so
     * this is one of the hottest paths in the app.
     */
    function stopPingLoop(): void {
        if (!pingLoopActive && pingTimer == null && failedPingCount === 0) {
            return
        }
        pingLoopActive = false
        failedPingCount = 0
        pingInFlight = false
        if (pingTimer != null) {
            clearTimeout(pingTimer)
            pingTimer = null
        }
    }

    /**
     * Marks the app online and stops recovery pings.
     *
     * @param source - What observed reachability. Defaults to `api-fetch`, the `useApiFetch` path.
     */
    function reportReachable(source: ConnectivityChangeSource = 'api-fetch'): void {
        setConnected(true, source)
        // Outside `setConnected`: a duplicate "online" signal must still cancel a stale loop.
        stopPingLoop()
    }

    /**
     * Marks the app offline and starts (or continues) the recovery ping loop.
     *
     * @param source - What observed the failure. Defaults to `api-fetch`, the `useApiFetch` path.
     */
    function reportTransportFailure(source: ConnectivityChangeSource = 'api-fetch'): void {
        setConnected(false, source)
        // Outside `setConnected`: a repeat failure while already offline must keep probing.
        ensurePingLoop()
    }

    /** {@link useApiFetch} entry point after any HTTP response (success or API error). */
    function notifyApiFetchReachable(): void {
        reportReachable('api-fetch')
    }

    /** {@link useApiFetch} entry point after a client transport failure (no HTTP response). */
    function notifyApiFetchTransportFailure(): void {
        reportTransportFailure('api-fetch')
    }

    /**
     * Schedules the next recovery ping if the loop is active.
     */
    function scheduleNextPing(): void {
        if (!pingLoopActive) {
            return
        }
        if (pingTimer != null) {
            clearTimeout(pingTimer)
        }
        const delayMs = nextConnectivityPingDelayMs(failedPingCount)
        pingTimer = setTimeout(() => {
            void runConnectivityPing()
        }, delayMs)
    }

    /**
     * Ensures a single recovery ping loop is running.
     * @param resetBackoff - When true (OS flipped back to online), retry sooner from the initial delay.
     */
    function ensurePingLoop(options?: { resetBackoff?: boolean }): void {
        if (options?.resetBackoff) {
            failedPingCount = 0
        }
        if (pingLoopActive) {
            if (options?.resetBackoff) {
                scheduleNextPing()
            }
            return
        }
        pingLoopActive = true
        scheduleNextPing()
    }

    /**
     * One reachability probe. Any HTTP response counts as reachable; only client transport
     * failures keep us offline and advance backoff.
     */
    async function runConnectivityPing(): Promise<void> {
        if (!pingLoopActive || pingInFlight) {
            return
        }
        pingInFlight = true
        try {
            const baseURL = useApiBaseUrl()
            const fetchOptions = await mergeCapacitorDesignerFetchOptions({
                method: 'GET',
                ...(baseURL ? { baseURL } : {}),
                retry: 0,
            })
            await $fetch(CONNECTIVITY_PING_PATH, fetchOptions)
            reportReachable('recovery-ping')
        } catch (err: unknown) {
            if (isLikelyClientTransportError(err)) {
                failedPingCount += 1
                scheduleNextPing()
                return
            }
            // HTTP error (4xx/5xx, force-update, etc.): the network path works.
            reportReachable('recovery-ping')
        } finally {
            pingInFlight = false
        }
    }

    /**
     * Applies Capacitor OS network status. OS-offline → API-offline + ping. OS-online while we
     * still think we are offline → reset backoff and probe (do not trust link-up alone).
     *
     * @param source - Which plugin delivered the status: the `@capacitor/network` listener /
     * initial read, or the `@capacitor/app` foreground re-check.
     */
    function applyCapacitorStatus(
        next: ConnectionStatus,
        source: Extract<
            ConnectivityChangeSource,
            'capacitor-network' | 'capacitor-resume'
        > = 'capacitor-network'
    ): void {
        connectionType.value = next.connectionType
        if (!next.connected) {
            reportTransportFailure(source)
            return
        }
        if (!connected.value) {
            // Link-up alone does not flip us online, so the eventual `reportReachable` comes from
            // the probe and is attributed to `recovery-ping`, not to this plugin.
            ensurePingLoop({ resetBackoff: true })
        }
    }

    /**
     * Bounded `Network.getStatus`. A hung bridge call must not keep `recheckConnectivity` or
     * store setup pending — that used to deadlock Nuxt plugins against Preferences restore.
     */
    async function readCapacitorStatus(): Promise<ConnectionStatus | null> {
        try {
            return await promiseWithTimeout(Network.getStatus(), NETWORK_STATUS_TIMEOUT_MS, null)
        } catch {
            return null
        }
    }

    /**
     * Re-reads OS status on demand and folds it back through {@link applyCapacitorStatus}.
     *
     * iOS does not reliably deliver `networkStatusChange` while the app is backgrounded (an
     * Airplane Mode toggle often only surfaces on foreground), and suspended timers stall the
     * recovery ping loop. Callers should invoke this when the app becomes active so both
     * directions re-settle: OS-offline marks us offline, OS-online while we still think we are
     * offline reschedules a probe from the initial delay.
     *
     * Deliberately does not probe when the OS and the store already agree we are online — the
     * next real `useApiFetch` reports a transport failure anyway, so an unconditional foreground
     * request would add traffic on every app switch for no new information.
     */
    async function recheckConnectivity(): Promise<void> {
        try {
            const status = await readCapacitorStatus()
            if (status) {
                applyCapacitorStatus(status, 'capacitor-resume')
            }
        } catch {
            // Bridge unavailable — leave current state; API traffic still decides.
        }
    }

    // Parallel, not sequenced: awaiting `addListener` before `getStatus` delayed the first
    // OS-offline read, so cold-start airplane mode stayed optimistic-online and the toast watcher
    // never saw a flip.
    void Network.addListener('networkStatusChange', (next) =>
        applyCapacitorStatus(next, 'capacitor-network')
    ).catch(() => {
        // Listener attach must not reject unhandled; getStatus below still decides.
    })
    void readCapacitorStatus().then((status) => {
        if (status) {
            applyCapacitorStatus(status, 'capacitor-network')
        }
    })

    return {
        connected,
        connectionType,
        status,
        lastChangeSource,
        changedByNativePlugin,
        recheckConnectivity,
        reportReachable,
        reportTransportFailure,
        notifyApiFetchReachable,
        notifyApiFetchTransportFailure,
    }
})
